import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { contactInfo } from '../lib/data';
import PageMeta from '../components/PageMeta';

const testimonials = [
  {
    id: 1,
    quote: "As a couple we had been trying to conceive for 2 years after having our son and had booked in with a fertility clinic when a friend recommended Ali to us as they had consulted with her when trying to conceive. While awaiting out appointment with the fertility clinic we fell pregnant after following Alis Nutrition and supplement plan for 2 months. Ali helped us to reduce inflammation, improve sperm motility and set us on a path of low stress and specific nutrients for egg health and sperm health.",
    name: null,
    detail: null,
  },
  {
    id: 2,
    quote: "I had been suffering with ongoing urinary tract burning pain for a number of years with a number of consultants and no improvements until I met with Ali and she did a urinary microbiome test and gave me a plan from the results. I am now symptom free.",
    name: "David",
    detail: "Limerick",
  },
  {
    id: 4,
    quote: "I went to Ali after I was diagnosed with Non-alcohol Fatty Liver disease and high cholesterol. I had not been feeling myself for months before going to Ali. What struck me from my first appointment was how knowledgeable Ali was! My treatment was so specific to me and easy to follow. Ali's support and guidance were very reassuring throughout. In a few short months under Ali's guidance I was able to reverse my Fatty Liver diagnosis and reduce my cholesterol. I have energy now that I haven't had in years and I can honestly say I feel 10 years younger. I cannot recommend Ali highly enough.",
    name: "Niamh",
    detail: "Cobh, Co Cork",
  },
  {
    id: 5,
    quote: "I have been going to Ali for years , wouldn't go past her , she is the best at Dry Needling and by far the most thorough",
    name: "Brendan",
    detail: "Cobh",
  },
  {
    id: 6,
    quote: "Ali has given me so much knowledge on how I can improve my health and guided me on managing my diabetes with food changes and exercise to avoid going on medication",
    name: "Christine",
    detail: "Cobh",
  },
  {
    id: 7,
    quote: "Great team, always welcomed by Kate, and Ali has been looking after my injuries for a few years. Recently attended Brian and had a great deep tissue treatment.",
    name: null,
    detail: null,
  },
  {
    id: 8,
    quote: "I recently went for a pregnancy massage with Joy and it was absolutely amazing. Joy was very skilled, gentle , and attentive to my needs. She explained everything clearly, made me feel completely comfortable, and focused on exactly the areas that were bothering me. By the end of the session, my neck and back pain had completely melted away. I left feeling relaxed, supported, and so much lighter",
    name: null,
    detail: null,
  },
  {
    id: 9,
    quote: "Recently attended Ali for digestive discomfort and ongoing heartburn, Ali directed me on some digestive changes, supplements to help heal the digestion and my heartburn is not a problem now- Ali is very thorough in her consultation and explained what was triggering the symptoms so well",
    name: "Damien",
    detail: "Cork",
  },
  {
    id: 10,
    quote: "I recently had my first visit with Joy for some physical therapy. It was a great experience overall. I felt comfortable and at ease throughout. Joy is extremely knowledgeable and even recommended exercises to be done in-between visits. I highly Joy to anyone looking for a NMT.",
    name: null,
    detail: null,
  },
];

const featured = testimonials[2]; // Niamh (id 4)
const grid = testimonials.filter(t => t.id !== featured.id);

const Testimonials = () => {
  return (
    <div data-testid="testimonials-page">
      <PageMeta
        title="Client Testimonials | Ali Dorgan"
        description="Read real testimonials from clients who have experienced Ali Dorgan's neuromuscular therapy and nutritional support in Cobh, Cork."
      />
      {/* Hero */}
      <section className="py-20 md:py-28 bg-cream-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
              Testimonials
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-charcoal leading-tight mb-8">
              Client Testimonials
            </h1>
            <p className="text-lg md:text-xl text-charcoal/70 leading-relaxed">
              Read what clients have shared about their experience with our team 
              across nutrition, injury treatment, and long-term health.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Testimonial */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <Card className="bg-sage-50 border-0 shadow-medium rounded-3xl" data-testid="featured-testimonial">
              <CardContent className="p-10 md:p-14">
                <Quote className="w-14 h-14 text-sage-300 mb-8" />
                <p className="font-serif text-xl md:text-2xl text-charcoal leading-relaxed mb-10">
                  "{featured.quote}"
                </p>
                <div className="border-t border-sage-200 pt-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-sage-200 rounded-full flex items-center justify-center">
                    <span className="font-serif text-lg text-sage-600 font-medium">
                      {featured.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-charcoal font-semibold text-lg">{featured.name}</p>
                    <p className="text-charcoal/50 text-sm">{featured.detail}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Testimonial Grid */}
      <section className="py-20 md:py-28 bg-cream-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {grid.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium transition-all duration-300 rounded-2xl" data-testid={`testimonial-card-${testimonial.id}`}>
                  <CardContent className="p-10 flex flex-col h-full">
                    <Quote className="w-10 h-10 text-sage-200 mb-6 shrink-0" />
                    <p className="text-charcoal/70 text-base leading-relaxed flex-1 mb-8">
                      "{testimonial.quote}"
                    </p>
                    {testimonial.name && (
                      <div className="border-t border-border/40 pt-6">
                        <p className="text-charcoal font-semibold">{testimonial.name}</p>
                        {testimonial.detail && (
                          <p className="text-charcoal/50 text-sm mt-1">{testimonial.detail}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-charcoal/50 text-sm mt-16 italic"
            data-testid="testimonials-disclaimer"
          >
            Individual results may vary.
          </motion.p>
        </div>
      </section>

      {/* Doctify Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <img
              src="/doctify-logo.png"
              alt="Doctify"
              className="h-12 w-12 mx-auto mb-6"
              data-testid="doctify-logo"
            />
            <h2 className="font-serif text-3xl md:text-4xl font-light text-charcoal mb-6">
              Independent Reviews
            </h2>
            <p className="text-charcoal/60 text-lg leading-relaxed mb-10">
              Ali is also listed on Doctify, where patients and professionals can view 
              verified reviews and learn more about her work.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-charcoal hover:bg-charcoal/90 text-white rounded-full px-10 py-6 font-semibold hover:-translate-y-0.5 transition-all duration-300"
              data-testid="doctify-cta-btn"
            >
              <a href="https://www.doctify.com/ie/specialist/ali-dorgan" target="_blank" rel="noopener noreferrer">
                View Ali on Doctify
              </a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-sage-900">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl md:text-5xl font-light text-cream-100 mb-8">
              Start Your Journey
            </h2>
            <p className="text-cream-200/80 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
              Ready to experience the benefits of neuromuscular and nutritional therapy?
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full px-10 py-6 gap-2 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                data-testid="testimonials-whatsapp-btn"
              >
                <a href={contactInfo.whatsapp} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5" />
                  Message Ali
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                className="bg-white text-charcoal hover:bg-cream-100 rounded-full px-10 py-6 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                data-testid="testimonials-book-btn"
              >
                <Link to="/services/injury-sports#practitioners">
                  Book Injury & Sports Treatment
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
